import { useCallback, useMemo } from 'react';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';

export const Margin = () => {
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const margin = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isRackingArea(area)) return;

    return NumUtils.metersToMmRounded(area.margin);
  }, [selectedAreas]);

  const onChange = useCallback(
    (newValueMm: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isRackingArea(area)) return;

      if (Number.isNaN(newValueMm)) return;
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('rackingArea.margin', newValue);
      fddmSvc.updateArea({
        ...area,
        margin: newValue,
      });
    },
    [fddmSvc, selectedAreas],
  );

  if (margin === undefined) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.area.margin'>
      <NumberInput
        value={margin}
        unit='mm'
        prefix={<GapHorizontalIcon />}
        sx={{ width: '100%' }}
        minValue={0}
        onChange={onChange}
      />
    </PropertiesLabel>
  );
};
